import {
  faWaveSquare,
  faUsers,
  faCapsules,
  faLink,
  faHeart,
  faStethoscope,
  faSync,
  faHeartbeat,
  faUserMd,
  faWheelchair,
  faAmbulance,
  faHospital,
  faSyringe,
  faPills,
  faBriefcaseMedical,
  faNotesMedical,
  faPrescriptionBottleAlt,
  faLungs,
  faBrain,
  faTooth,
  faBone,
  faVirus,
  faVial,
  faDna,
  faMicroscope,
  faXRay,
  faThermometerHalf,
  faLaptopMedical,
  faMobileAlt,
  faPhoneAlt,
  faClinicMedical,
  faFileAlt,
  faClock,
  faHeartPulse
} from '@fortawesome/free-solid-svg-icons';
import {
  Activity,
  Users,
  Stethoscope,
  Clock,
  Heart,
  HelpCircle,
  FileText
} from 'lucide-react';

export const FA_ICON_MAP = {
  faHeartbeat,
  faStethoscope,
  faUserMd,
  faWheelchair,
  faAmbulance,
  faHospital,
  faSyringe,
  faPills,
  faBriefcaseMedical,
  faNotesMedical,
  faPrescriptionBottleAlt,
  faLungs,
  faBrain,
  faTooth,
  faBone,
  faVirus,
  faVial,
  faDna,
  faMicroscope,
  faXRay,
  faThermometerHalf,
  faLaptopMedical,
  faMobileAlt,
  faPhoneAlt,
  faClinicMedical,
  faWaveSquare,
  faUsers,
  faCapsules,
  faLink,
  faHeart,
  faSync,
  faFileAlt,
  faClock,
  faHeartPulse
};

const LEGACY_FA_ICONS = {
  RPM: faWaveSquare,
  CCM: faUsers,
  PCM: faCapsules,
  RTM: faLink,
  CARDIAC: faHeart,
  CDM: faHeart
};

const LEGACY_LUCIDE_ICONS = {
  RPM: Activity,
  CCM: Users,
  PCM: Stethoscope,
  RTM: Clock,
  CARDIAC: Heart,
  CDM: Heart
};

export function normalizeProgramKey(name) {
  if (!name) return '';
  return String(name).trim().toLowerCase().replace(/\s+/g, '-');
}

/** Preferred UI order: RPM first, then CCM, then others. */
export const CARE_PROGRAM_DISPLAY_ORDER = {
  rpm: 0,
  ccm: 1,
  pcm: 2
};

export function getCareProgramDisplayRank(programOrKey) {
  const key = normalizeProgramKey(
    typeof programOrKey === 'string'
      ? programOrKey
      : (programOrKey?.short_name || programOrKey?.id || programOrKey?.name || '')
  );
  return CARE_PROGRAM_DISPLAY_ORDER[key] ?? 99;
}

export function sortCareProgramsForDisplay(programs) {
  return [...(Array.isArray(programs) ? programs : [])].sort((a, b) => {
    const rankDiff = getCareProgramDisplayRank(a) - getCareProgramDisplayRank(b);
    if (rankDiff !== 0) return rankDiff;
    const aName = String(a?.short_name || a?.id || a || '').toUpperCase();
    const bName = String(b?.short_name || b?.id || b || '').toUpperCase();
    return aName.localeCompare(bName);
  });
}

export function sortProgramKeysForDisplay(programKeys) {
  return [...(Array.isArray(programKeys) ? programKeys : [])].sort(
    (a, b) => getCareProgramDisplayRank(a) - getCareProgramDisplayRank(b)
  );
}

export function isActiveCareProgram(program) {
  const status = program?.status;
  if (status === null || status === undefined) return true;
  const s = String(status).trim().toLowerCase();
  return s === 'active' || s === 'a' || s === '1' || status === 1 || status === true;
}

export function filterActiveCarePrograms(programs) {
  return (Array.isArray(programs) ? programs : []).filter(isActiveCareProgram);
}

export function getFontAwesomeProgramIcon(program) {
  const iconKey = program?.icon;
  if (iconKey && FA_ICON_MAP[iconKey]) {
    return FA_ICON_MAP[iconKey];
  }
  const key = String(program?.short_name || program?.id || '').toUpperCase();
  return LEGACY_FA_ICONS[key] || faStethoscope;
}

export function getLucideProgramIcon(program) {
  const iconKey = program?.icon;
  if (iconKey && FA_ICON_MAP[iconKey]) {
    const lucideMap = {
      faWaveSquare: Activity,
      faHeartbeat: Activity,
      faHeartPulse: Activity,
      faUsers: Users,
      faStethoscope: Stethoscope,
      faClock: Clock,
      faHeart: Heart,
      faLink: Clock,
      faFileAlt: FileText,
      faCapsules: Stethoscope
    };
    if (lucideMap[iconKey]) return lucideMap[iconKey];
  }
  const key = String(program?.short_name || program?.id || '').toUpperCase();
  return LEGACY_LUCIDE_ICONS[key] || HelpCircle;
}

export function getEnrolledProgramNames(enrolledPrograms) {
  if (!Array.isArray(enrolledPrograms)) return [];
  return enrolledPrograms.map((item) => {
    if (typeof item === 'string') return item;
    return item?.short_name || item?.id || item?.name || '';
  }).filter(Boolean);
}

/** Resolve patient count for a program from API program_counts (keys vary by short name). */
export function resolveProgramPatientCount(programCounts, program) {
  const counts = programCounts && typeof programCounts === 'object' ? programCounts : {};
  const short = String(program?.short_name || program?.id || '').trim();
  const upper = short.toUpperCase();
  const normalized = normalizeProgramKey(short);
  return (
    counts[short] ??
    counts[upper] ??
    counts[normalized] ??
    counts[normalized?.toUpperCase()] ??
    0
  );
}

export function getProgramsForPractice(allPrograms, enrolledPrograms) {
  const enrolled = Array.isArray(enrolledPrograms) ? enrolledPrograms : [];
  const enrolledNames = getEnrolledProgramNames(enrolled).map((n) => normalizeProgramKey(n));

  if (enrolledNames.length === 0) {
    return [];
  }

  const activePrograms = filterActiveCarePrograms(allPrograms);
  const matched = activePrograms.filter((prog) => {
    const key = normalizeProgramKey(prog.short_name || prog.id);
    return enrolledNames.some((name) => name === key);
  });

  if (matched.length > 0) {
    return sortCareProgramsForDisplay(matched);
  }

  // Practice has enrollments but catalog match failed — use enrolled records directly
  return sortCareProgramsForDisplay(enrolled.map((item) => {
    if (typeof item === 'string') {
      return { short_name: item, full_name: item, status: 'Active' };
    }
    return item;
  }));
}

/** Build tabs/ribbon items from practice enrolled_programs only (no global catalog fallback). */
export function buildProgramTabsFromEnrolled(enrolledPrograms) {
  const enrolled = Array.isArray(enrolledPrograms) ? enrolledPrograms : [];
  return sortCareProgramsForDisplay(
    enrolled
      .filter((prog) => {
        if (typeof prog === 'string') return Boolean(prog.trim());
        return isActiveCareProgram(prog);
      })
      .map((prog) => {
        const shortName = typeof prog === 'string' ? prog : (prog.short_name || prog.id);
        const programObj = typeof prog === 'string' ? { short_name: prog, full_name: prog } : prog;
        const fullName = programObj.full_name || shortName;
        const tabId = normalizeProgramKey(shortName);
        return {
          id: tabId,
          label: String(shortName || '').trim().toUpperCase(),
          displayName: String(fullName || shortName || '').trim(),
          short_name: shortName,
          full_name: fullName,
          icon: getFontAwesomeProgramIcon(programObj),
          color: programObj.badge_color || '#64748b',
          cpt_codes: programObj.cpt_codes ?? null
        };
      })
  );
}

export function buildProgramTabs(programs) {
  return sortCareProgramsForDisplay(Array.isArray(programs) ? programs : []).map((prog) => ({
    id: normalizeProgramKey(prog.short_name || prog.id),
    label: String(prog.short_name || prog.id || '').toUpperCase(),
    short_name: prog.short_name,
    full_name: prog.full_name || prog.label || prog.short_name,
    icon: getFontAwesomeProgramIcon(prog),
    color: prog.badge_color || '#64748b'
  }));
}

/** Ribbon under practice name — enrolled programs from DB with patient counts. */
export function buildProgramRibbonItems(enrolledPrograms, programCounts = {}) {
  return buildProgramTabsFromEnrolled(enrolledPrograms).map((tab) => ({
    ...tab,
    count: resolveProgramPatientCount(programCounts, tab)
  }));
}

export function getProgramTagStyle(program) {
  const color = program?.badge_color || '#64748b';
  return {
    backgroundColor: `${color}25`,
    color,
    borderColor: `${color}40`
  };
}
